import {
  alertError,
  createJsonHeaders,
  logoutOnRequestOrResponseJson,
  logoutOnRequestOrResponseBlob,
} from '../../../api'
import { BASE_URL, BACKEND_URL } from '../../../configs'
import { savePopularHoursStatistics } from '../actions/popularHoursStatistics'
import { saveSupportStatistics } from '../actions/supportStatistics'
import { saveUsersStatistics } from '../actions/usersStatistics'

const appendDashboardDateParams = (params, { fromDate, toDate, isDefault } = {}) => {
  if (fromDate) params.append('fromDate', fromDate)
  if (toDate) params.append('toDate', toDate)
  if (isDefault) params.append('isDefault', 'true')
}

export const loadUsersStatistics = (botId, from, to, hourly) => {
  const loadDataUrl = !hourly
    ? `${BASE_URL}/chart/users/${botId}?from=${from}&to=${to}&allChannels=true`
    : `${BASE_URL}/chart/users/${botId}/last24hours?allChannels=true`
  return fetch(loadDataUrl, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => {
      saveUsersStatistics(responseJson.data)
    })
    .catch(err => {
      alertError('Not able to load user statistics data!')
    })
}

export const loadSupportStatistics = (botId, from, to) => {
  return fetch(`${BASE_URL}/chart/conversationCovering/${botId}?from=${from}&to=${to}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => {
      saveSupportStatistics(responseJson)
    })
    .catch(() => {
      alertError('Not able to load chatbot/support statistics data!')
    })
}

export const loadPopularHoursStatistics = (botId, from, to) => {
  return fetch(`${BASE_URL}/chart/hourlyConversation/${botId}?from=${from}&to=${to}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => {
      savePopularHoursStatistics(responseJson)
    })
    .catch(() => {
      alertError('Not able to load popular hours statistics data!')
    })
}

export const loadCarcoAdminDashboard = (botId, { fromDate, toDate, isDefault } = {}) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load Carco Admin Dashboard data!')
    })
}

export const exportDashboardXlsx = (botId, { fromDate, toDate, isDefault } = {}) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  return fetch(
    `${BACKEND_URL}/bot/${botId}/dashboard/export-xlsx?${params.toString()}`,
    {
      method: 'GET',
      headers: createJsonHeaders(),
      credentials: 'include',
    }
  )
    .then(response => logoutOnRequestOrResponseBlob(response))
    .catch(() => {
      alertError('Not able to export dashboard XLSX!')
    })
}

export const loadManagersList = botId => {
  return fetch(`${BACKEND_URL}/bot/${botId}/managers?botId=${botId}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load managers list!')
    })
}

export const loadDashboardOverviewUsers = (
  botId,
  { fromDate, toDate, isDefault, search, managerId, userStatus, archivedStatus, page = 1, size = 10, type } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (search) params.append('search', search)
  if (managerId) params.append('managerId', String(managerId))
  if (userStatus) params.append('userStatus', userStatus)
  if (page != null) params.append('page', String(page))
  if (size != null) params.append('size', String(size))
  if (type) params.append('type', type)
  if (archivedStatus) params.append('archivedStatus', archivedStatus)

  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/overview?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load dashboard users list!')
    })
}

export const exportDashboardOverviewUsersCsv = (
  botId,
  { fromDate, toDate, isDefault, search, managerId, userStatus, archivedStatus, page = 1, size = 10, type } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (search) params.append('search', search)
  if (managerId) params.append('managerId', String(managerId))
  if (userStatus) params.append('userStatus', userStatus)
  if (page != null) params.append('page', String(page))
  if (size != null) params.append('size', String(size))
  if (type) params.append('type', type)
  if (archivedStatus) params.append('archivedStatus', archivedStatus)

  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/overview/export-csv?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseBlob(response))
    .catch(() => {
      alertError('Not able to export dashboard users CSV!')
    })
}

export const loadSupervisionTable = (
  botId,
  { fromDate, toDate, isDefault, templateId, type, search, page = 1, size = 10, sentiment, userStatus } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (templateId) params.append('templateId', String(templateId))
  if (type) params.append('type', type)
  if (search) params.append('search', search)
  if (page != null) params.append('page', String(page))
  if (size != null) params.append('size', String(size))
  if (sentiment) params.append('sentiment', sentiment)
  if (userStatus) params.append('userStatus', userStatus)
  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/supervision/table-view?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load supervision table data!')
    })
}

export const exportSupervisionTableCsv = (
  botId,
  { fromDate, toDate, isDefault, templateId, type, search, page = 1, size = 10, sentiment, userStatus } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (templateId) params.append('templateId', String(templateId))
  if (type) params.append('type', type)
  if (search) params.append('search', search)
  if (page != null) params.append('page', String(page))
  if (size != null) params.append('size', String(size))
  if (sentiment) params.append('sentiment', sentiment)
  if (userStatus) params.append('userStatus', userStatus)

  return fetch(
    `${BACKEND_URL}/bot/${botId}/dashboard/supervision/table-view/export-csv?${params.toString()}`,
    {
      method: 'GET',
      headers: createJsonHeaders(),
      credentials: 'include',
    }
  )
    .then(response => logoutOnRequestOrResponseBlob(response))
    .catch(() => {
      alertError('Not able to export supervision table CSV!')
    })
}

export const loadSupervisionOptions = botId => {
  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/supervision/list`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load supervision options list!')
    })
}

export const loadSupervisionChart = (
  botId,
  { fromDate, toDate, isDefault, templateId, type } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (templateId) params.append('templateId', String(templateId))
  if (type) params.append('type', type)

  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/supervision/chart-view?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load supervision chart data!')
    })
}

export const loadAskAiTopics = botId => {
  return fetch(`${BACKEND_URL}/dashboard/ask-ai/topics`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load Ask AI topics!')
    })
}

export const loadAskAiTopicPrompts = (botId, topicId) => {
  return fetch(`${BACKEND_URL}/dashboard/ask-ai/topics/${topicId}/prompts`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load Ask AI topic prompts!')
    })
}

export const loadAskAiChatHistory = (botId, adminId, { page = 0, size = 10 } = {}) => {
  const params = new URLSearchParams()
  params.append('page', String(page))
  params.append('size', String(size))

  return fetch(
    `${BACKEND_URL}/dashboard/ask-ai/bot/${botId}/admin/${adminId}/conversations?${params.toString()}&sort=sentAt,desc`,
    {
      method: 'GET',
      headers: createJsonHeaders(),
      credentials: 'include',
    }
  )
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load Ask AI chat history!')
    })
}

export const loadTotalChatList = (
  botId,
  { fromDate, toDate, search, managerId, status, userStatus, roleId, page = 1, size = 10, isDefault } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (search) params.append('search', search)
  if (managerId) params.append('managerId', String(managerId))
  if (status) params.append('status', status)
  if (userStatus) params.append('userStatus', userStatus)
  if (roleId) params.append('roleId', String(roleId))
  params.append('page', String(page))
  params.append('size', String(size))

  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/chat?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load total chat list!')
    })
}

export const exportTotalChatCsv = (
  botId,
  { fromDate, toDate, search, managerId, status, userStatus, roleId, page = 1, size = 10, isDefault } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (search) params.append('search', search)
  if (managerId) params.append('managerId', String(managerId))
  if (status) params.append('status', status)
  if (userStatus) params.append('userStatus', userStatus)
  if (roleId) params.append('roleId', String(roleId))
  params.append('page', String(page))
  params.append('size', String(size))

  return fetch(`${BACKEND_URL}/bot/${botId}/dashboard/chat/export-csv?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseBlob(response))
    .catch(() => {
      alertError('Not able to export total chat list CSV!')
    })
}

export const loadTopQueriedTopics = (
  botId,
  { fromDate, toDate, search, sortBy1, sortDir1, sortBy2, sortDir2, page = 1, size = 10, isDefault } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (search) params.append('search', search)
  if (sortBy1) params.append('sortBy1', sortBy1)
  if (sortDir1) params.append('sortDir1', sortDir1)
  if (sortBy2) params.append('sortBy2', sortBy2)
  if (sortDir2) params.append('sortDir2', sortDir2)
  params.append('page', String(page))
  params.append('size', String(size))

  return fetch(`${BACKEND_URL}/dashboard/top-queried-topics/bot/${botId}/table-view?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(responseJson => responseJson)
    .catch(() => {
      alertError('Not able to load top queried topics!')
    })
}

export const exportTopQueriedTopicsCsv = (
  botId,
  { fromDate, toDate, search, sortBy1, sortDir1, sortBy2, sortDir2, isDefault } = {}
) => {
  const params = new URLSearchParams()
  appendDashboardDateParams(params, { fromDate, toDate, isDefault })
  if (search) params.append('search', search)
  if (sortBy1) params.append('sortBy1', sortBy1)
  if (sortDir1) params.append('sortDir1', sortDir1)
  if (sortBy2) params.append('sortBy2', sortBy2)
  if (sortDir2) params.append('sortDir2', sortDir2)

  return fetch(`${BACKEND_URL}/dashboard/top-queried-topics/bot/${botId}/table-view/export-csv?${params.toString()}`, {
    method: 'GET',
    headers: createJsonHeaders(),
    credentials: 'include',
  })
    .then(response => logoutOnRequestOrResponseBlob(response))
    .catch(() => {
      alertError('Not able to export top queried topics CSV!')
    })
}
